import React, { useCallback } from 'react';
import cx from 'classnames';
import T from 'i18n-react';

import cypressIds from 'Components/cypress_ids.json';
import ClearAllButton from 'Components/pages/VehiclesList/FiltersHeader/ClearAllButton';
import { SavedSearchDrawer } from 'Components/SavedSearch/SavedSearchDrawer/SavedSearchDrawer';
import { SavedSearchPill } from 'Components/SavedSearch/SavedSearchPill/SavedSearchPill';
import {
	triggerSavedSearchPillClickGAEvent,
	triggerSavedSearchPillHoverGAEvent,
} from 'Components/SavedSearch/SavedSearchPill/SavedSearchPill.helpers';
import SavedFiltersButton from 'Components/shared/SavedFilters/SavedFiltersButton/SavedFiltersButton';
import { useCurrentSelectedFilter } from 'Components/shared/SavedFilters/useCurrentSelectedFilter';
import { useSetIsUniqueFilter } from 'Components/shared/SavedFilters/useSetIsUniqueFilter';
import texts from 'Components/texts.json';
import { useSeenFilter } from 'Components/VehicleList/SeenFilter';
import { getVehiclesCount, joinSortParams } from 'Components/VehicleList/VehicleList.helpers';
import VehicleListFiltersButton from 'Components/VehicleList/VehicleListFiltersButton/VehicleListFiltersButton';
import VehicleSort from 'Components/VehicleList/VehicleSort/VehicleSort';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerClearAllFiltersButtonClickedEvent, triggerFilterButtonClickedEvent } from 'Services/analytics/events';
import { useListType } from 'Stores/ListStore/ListStore';
import { useCurrentFilter } from 'Stores/SavedFiltersStore/SavedFiltersStore';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import { useIsMounted } from 'Utilities/hooks/useIsMounted';
import { useResetFilters as useResetFiltersAndCurrentFilter } from 'Utilities/hooks/vehicleList/useResetFilters';
import { VIEWING_FILTER } from 'Utilities/seenVehicles/seenVehicles';

import { BulkBidding } from './BulkBidding/BulkBidding';
import { BulkBiddingModalProvider } from './BulkBidding/BulkBiddingModalProvider';
import { VehicleListDownloadCsv } from './VehicleListDownloadCsv/VehicleListDownloadCsv';

import styles from './FiltersHeader.module.scss';

T.setTexts(texts);

interface FiltersHeaderProps {
	dealerLatLng: (Nullable<number> | undefined)[];
	dealerPostcode?: string;
	handleSorting: () => void;
	initialLoad: boolean;
	isLoading: boolean;
	noVehiclesFound: boolean;
	sortParams: {
		order: string;
		sort: string;
	};
	togglePanelFilters: () => void;
	vehiclesCount: number;
}

const FiltersHeader: React.FC<FiltersHeaderProps> = ({
	dealerLatLng,
	dealerPostcode,
	initialLoad = false,
	isLoading,
	noVehiclesFound,
	sortParams,
	togglePanelFilters,
	vehiclesCount,
}) => {
	const listType = useListType();
	useSetIsUniqueFilter();
	useCurrentSelectedFilter();
	const {
		minWidth: { breakpointContent: isDesktop },
	} = useBreakpoint();
	const isBulkBiddingCsvEnabled = useFeatureToggle(FEATURES.bulkBiddingCsv);
	const isMounted = useIsMounted();
	const showToolbarClearAll = isDesktop;
	const isMobileOrTablet = !isDesktop;
	const showDownloadButton = isDesktop;
	const showSidebarFiltersButton = isMobileOrTablet;
	const [seenFilter, setSeenFilter] = useSeenFilter();
	const showVehiclesCountLabel = isDesktop && vehiclesCount > 0 && seenFilter === VIEWING_FILTER.ALL;
	const resetFiltersAndCurrentFilter = useResetFiltersAndCurrentFilter();
	const hasActiveSavedSearch = !!useCurrentFilter();
	const { vehicleSortDesktop } = cypressIds.filters;
	const shouldIncludeMobileSavedSearchDrawer = isMounted && isMobileOrTablet;

	const shouldShowBulkBidCSV = isBulkBiddingCsvEnabled && listType === 'auction';

	const onFilterButtonClick = useCallback(() => {
		triggerFilterButtonClickedEvent();
		togglePanelFilters();
	}, [togglePanelFilters]);

	const onClearFilters = useCallback(() => {
		triggerClearAllFiltersButtonClickedEvent();
		resetFiltersAndCurrentFilter();
		setSeenFilter(VIEWING_FILTER.ALL);
	}, [resetFiltersAndCurrentFilter, setSeenFilter]);

	const onSavedSearchPillClick = () => triggerSavedSearchPillClickGAEvent(hasActiveSavedSearch);
	const onSavedSearchPillMouseEnter = () => triggerSavedSearchPillHoverGAEvent(hasActiveSavedSearch);

	return (
		<div className={cx(styles.filtersHeader)}>
			{showToolbarClearAll && (
				<div className={styles.headerSectionWrapper}>
					<div className={styles.desktopSection}>
						<h3 className={styles.desktopTitle}>{T.translate('vehicleList.filters.title')}</h3>
						<ClearAllButton
							shouldDisplayTextButton
							onClick={onClearFilters}
							text={T.translate('vehicleList.filters.clearFilters')}
						/>
					</div>
				</div>
			)}

			{showSidebarFiltersButton && (
				<div className={cx(styles.headerSectionWrapper, styles.sidebarHeader)}>
					<VehicleListFiltersButton
						{...{
							className: styles.vehicleListFiltersButton,
							onFilterButtonClick,
							touched: initialLoad,
						}}
					/>
				</div>
			)}

			{showVehiclesCountLabel && (
				<div
					className={cx(
						styles.headerSectionWrapper,
						{ [styles.sectionVehicleCount]: isMobileOrTablet },
						{ [styles.vehicleCountZero]: isMobileOrTablet && noVehiclesFound },
					)}
				>
					<div
						className={cx(styles.vehicleCount, {
							[styles.dataLoading]: isLoading,
							[styles.preventFocus]: initialLoad,
						})}
					>
						{vehiclesCount === -1
							? 'Showing matching vehicles'
							: getVehiclesCount('vehicleList.showingVehicle', vehiclesCount)}
					</div>
				</div>
			)}

			{shouldIncludeMobileSavedSearchDrawer && (
				<div className={cx(styles.headerSectionWrapper, styles.sidebarHeader)}>
					<SavedSearchPill onClick={onSavedSearchPillClick} onMouseEnter={onSavedSearchPillMouseEnter} />
					<SavedSearchDrawer />
				</div>
			)}

			<div className={cx(styles.headerSectionWrapper)}>
				{isMobileOrTablet && <SavedFiltersButton />}
				{isDesktop && (
					<VehicleSort
						className={styles.vehicleSort}
						cypressId={vehicleSortDesktop}
						dealerLatLng={dealerLatLng}
						dealerPostcode={dealerPostcode}
						selected={joinSortParams(sortParams)}
					/>
				)}
				{showDownloadButton && (
					<div className={cx(styles.downloadableVehicleListSection)}>
						<VehicleListDownloadCsv />
						{shouldShowBulkBidCSV ? (
							<BulkBiddingModalProvider>
								<BulkBidding />
							</BulkBiddingModalProvider>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
};

export default FiltersHeader;
